import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useParams } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';
import api from '../../services/api';

const LIMIT_IMAGE_MB = 0.85;
const MAX_OUTPUT_SIDE = 1400;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.9) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }

      reject(new Error('Não foi possível gerar a imagem editada.'));
    }, type, quality);
  });
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    reader.readAsDataURL(file);
  });
}

function carregarImagem(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Não foi possível abrir a imagem.'));
    image.src = url;
  });
}

async function normalizarArquivoImagem(file) {
  if (!file) return null;

  const nome = String(file.name || '').toLowerCase();
  const isHeic =
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    nome.endsWith('.heic') ||
    nome.endsWith('.heif');

  if (!isHeic) return file;

  const convertido = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92
  });

  const blob = Array.isArray(convertido) ? convertido[0] : convertido;

  return new File(
    [blob],
    String(file.name || `camera-${Date.now()}.heic`).replace(/\.(heic|heif)$/i, '.jpg'),
    {
      type: 'image/jpeg',
      lastModified: Date.now()
    }
  );
}

const ASPECTOS = {
  quadrado: {
    label: '1:1',
    width: 1200,
    height: 1200
  },
  paisagem: {
    label: '4:3',
    width: 1400,
    height: 1050
  },
  retrato: {
    label: '3:4',
    width: 1050,
    height: 1400
  },
  original: {
    label: 'Original'
  }
};

export default function MobileCamera() {
  const { sessionId } = useParams();

  const inputRef = useRef(null);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const objectUrlRef = useRef('');
  const pointerRef = useRef(null);

  const [photoReady, setPhotoReady] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [rotation, setRotation] = useState(0);
  const [flipX, setFlipX] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [aspect, setAspect] = useState('retrato');

  const sessionValid = useMemo(() => {
    const value = String(sessionId || '').trim();

    return (
      value.length >= 6 &&
      value.length <= 180 &&
      /^[a-zA-Z0-9_-]+$/.test(value)
    );
  }, [sessionId]);

  const limparObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
  }, []);

  const resetEditor = useCallback(() => {
    setRotation(0);
    setFlipX(false);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setAspect('retrato');
  }, []);

  const limparFoto = useCallback(() => {
    limparObjectUrl();
    imageRef.current = null;
    setPhotoReady(false);
    resetEditor();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, [limparObjectUrl, resetEditor]);

  useEffect(() => {
    return () => {
      limparObjectUrl();
    };
  }, [limparObjectUrl]);

  const obterTamanhoCanvas = useCallback(() => {
    const image = imageRef.current;

    if (!image) {
      return {
        width: 1050,
        height: 1400
      };
    }

    if (aspect !== 'original') {
      return {
        width: ASPECTOS[aspect].width,
        height: ASPECTOS[aspect].height
      };
    }

    const invertido = Math.abs(rotation % 180) === 90;
    const naturalWidth = invertido ? image.naturalHeight : image.naturalWidth;
    const naturalHeight = invertido ? image.naturalWidth : image.naturalHeight;
    const ratio = naturalWidth / Math.max(1, naturalHeight);

    if (ratio >= 1) {
      return {
        width: MAX_OUTPUT_SIDE,
        height: Math.max(1, Math.round(MAX_OUTPUT_SIDE / ratio))
      };
    }

    return {
      width: Math.max(1, Math.round(MAX_OUTPUT_SIDE * ratio)),
      height: MAX_OUTPUT_SIDE
    };
  }, [aspect, rotation]);

  const desenharEditor = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image || !photoReady) return;

    const { width, height } = obterTamanhoCanvas();

    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;

    const context = canvas.getContext('2d', {
      alpha: false
    });

    if (!context) return;

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.fillStyle = '#080f1f';
    context.fillRect(0, 0, width, height);

    const normalizedRotation = ((rotation % 360) + 360) % 360;
    const isQuarterTurn =
      normalizedRotation === 90 ||
      normalizedRotation === 270;

    const rotatedWidth = isQuarterTurn
      ? image.naturalHeight
      : image.naturalWidth;

    const rotatedHeight = isQuarterTurn
      ? image.naturalWidth
      : image.naturalHeight;

    const coverScale = Math.max(
      width / Math.max(1, rotatedWidth),
      height / Math.max(1, rotatedHeight)
    );

    const finalScale = coverScale * zoom;
    const renderedWidth = rotatedWidth * finalScale;
    const renderedHeight = rotatedHeight * finalScale;

    const maxOffsetX = Math.max(0, (renderedWidth - width) / 2);
    const maxOffsetY = Math.max(0, (renderedHeight - height) / 2);

    const safeOffsetX = clamp(offset.x, -maxOffsetX, maxOffsetX);
    const safeOffsetY = clamp(offset.y, -maxOffsetY, maxOffsetY);

    context.save();
    context.translate(
      width / 2 + safeOffsetX,
      height / 2 + safeOffsetY
    );
    context.rotate((normalizedRotation * Math.PI) / 180);
    context.scale(flipX ? -1 : 1, 1);

    context.drawImage(
      image,
      -(image.naturalWidth * finalScale) / 2,
      -(image.naturalHeight * finalScale) / 2,
      image.naturalWidth * finalScale,
      image.naturalHeight * finalScale
    );

    context.restore();
  }, [
    flipX,
    obterTamanhoCanvas,
    offset.x,
    offset.y,
    photoReady,
    rotation,
    zoom
  ]);

  useEffect(() => {
    desenharEditor();
  }, [desenharEditor]);

  const abrirCamera = useCallback(() => {
    if (!sessionValid || sending || loadingPhoto) return;

    setErrorMessage('');
    setMessage('');

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  }, [loadingPhoto, sending, sessionValid]);

  const handleCapture = useCallback(async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setLoadingPhoto(true);
    setErrorMessage('');
    setMessage('Preparando a foto para edição...');

    try {
      const normalizedFile = await normalizarArquivoImagem(file);

      limparObjectUrl();

      const objectUrl = URL.createObjectURL(normalizedFile);
      objectUrlRef.current = objectUrl;

      const image = await carregarImagem(objectUrl);

      imageRef.current = image;
      resetEditor();
      setPhotoReady(true);
      setMessage('Arraste para enquadrar, use o zoom e toque em Enviar.');
    } catch (error) {
      console.error('[MobileCamera] Erro ao preparar foto:', error);
      limparFoto();
      setErrorMessage(
        error?.message ||
        'Não foi possível abrir essa foto. Tente novamente.'
      );
      setMessage('');
    } finally {
      setLoadingPhoto(false);

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  }, [
    limparFoto,
    limparObjectUrl,
    resetEditor
  ]);

  const handlePointerDown = useCallback((event) => {
    const canvas = canvasRef.current;

    if (!canvas || !photoReady || sending) return;

    canvas.setPointerCapture?.(event.pointerId);

    pointerRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      offsetX: offset.x,
      offsetY: offset.y
    };
  }, [
    offset.x,
    offset.y,
    photoReady,
    sending
  ]);

  const handlePointerMove = useCallback((event) => {
    const canvas = canvasRef.current;
    const start = pointerRef.current;

    if (!canvas || !start || start.pointerId !== event.pointerId) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / Math.max(1, rect.width);
    const scaleY = canvas.height / Math.max(1, rect.height);

    setOffset({
      x: start.offsetX + (event.clientX - start.x) * scaleX,
      y: start.offsetY + (event.clientY - start.y) * scaleY
    });
  }, []);

  const handlePointerUp = useCallback((event) => {
    const canvas = canvasRef.current;

    if (canvas) {
      canvas.releasePointerCapture?.(event.pointerId);
    }

    pointerRef.current = null;
  }, []);

  const alterarRotacao = useCallback((delta) => {
    setRotation((current) => current + delta);
    setOffset({ x: 0, y: 0 });
  }, []);

  const alterarAspecto = useCallback((novoAspecto) => {
    setAspect(novoAspecto);
    setOffset({ x: 0, y: 0 });
  }, []);

  const redefinirEdicao = useCallback(() => {
    resetEditor();
    setMessage('Edição redefinida.');
  }, [resetEditor]);

  const enviarFoto = useCallback(async () => {
    const canvas = canvasRef.current;

    if (!sessionValid) {
      setErrorMessage('Sessão inválida. Escaneie novamente o QR Code.');
      return;
    }

    if (!canvas || !photoReady || sending) return;

    setSending(true);
    setErrorMessage('');
    setMessage('Gerando e comprimindo a foto...');

    try {
      desenharEditor();

      const editedBlob = await canvasToBlob(
        canvas,
        'image/jpeg',
        0.9
      );

      const editedFile = new File(
        [editedBlob],
        `camera-${Date.now()}.jpg`,
        {
          type: 'image/jpeg',
          lastModified: Date.now()
        }
      );

      const compressedFile = await imageCompression(editedFile, {
        maxSizeMB: LIMIT_IMAGE_MB,
        maxWidthOrHeight: 1600,
        initialQuality: 0.88,
        useWebWorker: true,
        fileType: 'image/jpeg'
      });

      const base64data = await fileToDataUrl(compressedFile);

      setMessage('Enviando para o computador...');

      await api.post('/ordens-servico/api/sync-camera', {
        sessionId,
        image: base64data
      });

      setSentCount((count) => count + 1);
      setMessage('Foto enviada. Você já pode tirar outra.');
      limparFoto();
    } catch (error) {
      console.error('[MobileCamera] Erro ao enviar foto:', error);

      setErrorMessage(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao enviar foto. Tente novamente.'
      );

      setMessage('');
    } finally {
      setSending(false);
    }
  }, [
    desenharEditor,
    limparFoto,
    photoReady,
    sending,
    sessionId,
    sessionValid
  ]);

  if (!sessionValid) {
    return (
      <main style={styles.page}>
        <section style={styles.card}>
          <div style={styles.errorIcon}>!</div>
          <h1 style={styles.title}>Sessão inválida</h1>
          <p style={styles.subtitle}>
            Volte ao computador e escaneie novamente o QR Code.
          </p>
        </section>
      </main>
    );
  }

  return (
    <main style={styles.page}>
      <section style={styles.card}>
        <header style={styles.header}>
          <div style={styles.brandIcon}>📸</div>
          <div>
            <h1 style={styles.title}>Câmera de anexos</h1>
            <p style={styles.subtitle}>
              Use este mesmo QR Code para enviar várias fotos.
            </p>
          </div>
        </header>

        <div style={styles.sessionBar}>
          <span style={styles.sessionDot} />
          <span>Sessão ativa</span>
          <strong>{sentCount} enviada(s)</strong>
        </div>

        {errorMessage ? (
          <div style={styles.errorBox}>
            {errorMessage}
          </div>
        ) : null}

        {message ? (
          <div style={styles.infoBox}>
            {message}
          </div>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCapture}
          style={styles.hiddenInput}
        />

        {!photoReady ? (
          <div style={styles.captureArea}>
            <div style={styles.cameraCircle}>📷</div>

            <h2 style={styles.captureTitle}>
              {loadingPhoto
                ? 'Abrindo a foto...'
                : sentCount > 0
                  ? 'Tirar outra foto'
                  : 'Tirar a primeira foto'}
            </h2>

            <p style={styles.captureText}>
              A foto será aberta para cortar, girar e ajustar antes do envio.
            </p>

            <button
              type="button"
              onClick={abrirCamera}
              disabled={loadingPhoto || sending}
              style={{
                ...styles.primaryButton,
                ...(loadingPhoto || sending
                  ? styles.disabledButton
                  : {})
              }}
            >
              {loadingPhoto
                ? 'Preparando...'
                : 'Abrir câmera'}
            </button>
          </div>
        ) : (
          <>
            <div style={styles.editorHeader}>
              <div>
                <strong style={styles.editorTitle}>Editar foto</strong>
                <span style={styles.editorHint}>
                  Arraste a imagem para ajustar o recorte
                </span>
              </div>

              <button
                type="button"
                onClick={limparFoto}
                disabled={sending}
                style={styles.textButton}
              >
                Descartar
              </button>
            </div>

            <div style={styles.canvasFrame}>
              <canvas
                ref={canvasRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                style={styles.canvas}
              />
              <div style={styles.cropBadge}>
                Área que será enviada
              </div>
            </div>

            <div style={styles.controlBlock}>
              <div style={styles.controlLabelRow}>
                <span style={styles.controlLabel}>Formato do corte</span>
              </div>

              <div style={styles.segmented}>
                {Object.entries(ASPECTOS).map(([key, item]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => alterarAspecto(key)}
                    disabled={sending}
                    style={{
                      ...styles.segmentButton,
                      ...(aspect === key
                        ? styles.segmentButtonActive
                        : {})
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.controlBlock}>
              <div style={styles.controlLabelRow}>
                <span style={styles.controlLabel}>Zoom</span>
                <strong style={styles.zoomValue}>
                  {Math.round(zoom * 100)}%
                </strong>
              </div>

              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={zoom}
                disabled={sending}
                onChange={(event) => {
                  setZoom(Number(event.target.value));
                }}
                style={styles.range}
              />
            </div>

            <div style={styles.toolGrid}>
              <button
                type="button"
                onClick={() => alterarRotacao(-90)}
                disabled={sending}
                style={styles.toolButton}
              >
                ↶ Girar
              </button>

              <button
                type="button"
                onClick={() => alterarRotacao(90)}
                disabled={sending}
                style={styles.toolButton}
              >
                ↷ Girar
              </button>

              <button
                type="button"
                onClick={() => setFlipX((value) => !value)}
                disabled={sending}
                style={{
                  ...styles.toolButton,
                  ...(flipX ? styles.toolButtonActive : {})
                }}
              >
                ⇋ Espelhar
              </button>

              <button
                type="button"
                onClick={redefinirEdicao}
                disabled={sending}
                style={styles.toolButton}
              >
                ⟲ Redefinir
              </button>
            </div>

            <div style={styles.actions}>
              <button
                type="button"
                onClick={abrirCamera}
                disabled={sending}
                style={styles.secondaryButton}
              >
                Tirar novamente
              </button>

              <button
                type="button"
                onClick={enviarFoto}
                disabled={sending}
                style={{
                  ...styles.sendButton,
                  ...(sending ? styles.disabledButton : {})
                }}
              >
                {sending
                  ? 'Enviando...'
                  : 'Enviar foto'}
              </button>
            </div>
          </>
        )}

        <footer style={styles.footer}>
          <span>
            Não feche esta página enquanto estiver enviando.
          </span>
          <small>Sessão: {sessionId}</small>
        </footer>
      </section>
    </main>
  );
}

const styles = {
  page: {
    boxSizing: 'border-box',
    minHeight: '100svh',
    padding: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    background:
      'radial-gradient(circle at top, #172554 0%, #0f172a 34%, #020617 100%)',
    color: '#f8fafc',
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },

  card: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '620px',
    margin: '0 auto',
    padding: '18px',
    border: '1px solid rgba(148,163,184,.20)',
    borderRadius: '24px',
    background: 'rgba(15,23,42,.94)',
    boxShadow: '0 24px 70px rgba(0,0,0,.38)',
    backdropFilter: 'blur(18px)'
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px'
  },

  brandIcon: {
    width: '48px',
    height: '48px',
    flex: '0 0 48px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, #4f46e5, #2563eb)',
    fontSize: '23px',
    boxShadow: '0 12px 28px rgba(79,70,229,.35)'
  },

  title: {
    margin: 0,
    color: '#ffffff',
    fontSize: '22px',
    lineHeight: 1.1,
    fontWeight: 900,
    letterSpacing: '-.03em'
  },

  subtitle: {
    margin: '5px 0 0',
    color: '#94a3b8',
    fontSize: '13px',
    lineHeight: 1.45
  },

  sessionBar: {
    minHeight: '38px',
    marginBottom: '14px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: '1px solid rgba(16,185,129,.24)',
    borderRadius: '12px',
    background: 'rgba(6,78,59,.22)',
    color: '#a7f3d0',
    fontSize: '12px'
  },

  sessionDot: {
    width: '8px',
    height: '8px',
    borderRadius: '999px',
    background: '#22c55e',
    boxShadow: '0 0 0 5px rgba(34,197,94,.12)'
  },

  infoBox: {
    marginBottom: '12px',
    padding: '10px 12px',
    border: '1px solid rgba(56,189,248,.22)',
    borderRadius: '12px',
    background: 'rgba(14,116,144,.15)',
    color: '#bae6fd',
    fontSize: '12px',
    lineHeight: 1.45
  },

  errorBox: {
    marginBottom: '12px',
    padding: '10px 12px',
    border: '1px solid rgba(248,113,113,.26)',
    borderRadius: '12px',
    background: 'rgba(127,29,29,.20)',
    color: '#fecaca',
    fontSize: '12px',
    lineHeight: 1.45
  },

  errorIcon: {
    width: '52px',
    height: '52px',
    margin: '6px auto 14px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '18px',
    background: 'rgba(239,68,68,.16)',
    color: '#fca5a5',
    fontSize: '28px',
    fontWeight: 900
  },

  hiddenInput: {
    display: 'none'
  },

  captureArea: {
    minHeight: '390px',
    padding: '30px 18px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px dashed rgba(148,163,184,.28)',
    borderRadius: '20px',
    background:
      'linear-gradient(180deg, rgba(30,41,59,.72), rgba(15,23,42,.42))',
    textAlign: 'center'
  },

  cameraCircle: {
    width: '86px',
    height: '86px',
    marginBottom: '20px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '999px',
    background:
      'linear-gradient(135deg, rgba(79,70,229,.30), rgba(37,99,235,.17))',
    border: '1px solid rgba(129,140,248,.28)',
    fontSize: '38px'
  },

  captureTitle: {
    margin: 0,
    color: '#ffffff',
    fontSize: '21px',
    fontWeight: 900
  },

  captureText: {
    maxWidth: '350px',
    margin: '9px 0 22px',
    color: '#94a3b8',
    fontSize: '13px',
    lineHeight: 1.55
  },

  primaryButton: {
    minWidth: '210px',
    minHeight: '52px',
    padding: '0 24px',
    border: 0,
    borderRadius: '999px',
    background: 'linear-gradient(135deg, #4f46e5, #2563eb)',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 14px 32px rgba(37,99,235,.30)'
  },

  disabledButton: {
    opacity: 0.58,
    cursor: 'not-allowed'
  },

  editorHeader: {
    marginBottom: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px'
  },

  editorTitle: {
    display: 'block',
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: 900
  },

  editorHint: {
    display: 'block',
    marginTop: '3px',
    color: '#94a3b8',
    fontSize: '11px'
  },

  textButton: {
    padding: '8px 10px',
    border: 0,
    background: 'transparent',
    color: '#fca5a5',
    fontSize: '12px',
    fontWeight: 800,
    cursor: 'pointer'
  },

  canvasFrame: {
    position: 'relative',
    width: '100%',
    minHeight: '250px',
    maxHeight: '54svh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    border: '1px solid rgba(148,163,184,.22)',
    borderRadius: '18px',
    background: '#020617',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.02)'
  },

  canvas: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: '54svh',
    width: 'auto',
    height: 'auto',
    touchAction: 'none',
    cursor: 'grab',
    userSelect: 'none'
  },

  cropBadge: {
    position: 'absolute',
    left: '10px',
    bottom: '10px',
    padding: '6px 9px',
    borderRadius: '999px',
    background: 'rgba(2,6,23,.74)',
    color: '#cbd5e1',
    fontSize: '10px',
    fontWeight: 800,
    pointerEvents: 'none'
  },

  controlBlock: {
    marginTop: '14px'
  },

  controlLabelRow: {
    marginBottom: '7px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  controlLabel: {
    color: '#cbd5e1',
    fontSize: '11px',
    fontWeight: 850,
    textTransform: 'uppercase',
    letterSpacing: '.055em'
  },

  zoomValue: {
    color: '#93c5fd',
    fontSize: '11px'
  },

  segmented: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '6px'
  },

  segmentButton: {
    minHeight: '38px',
    padding: '0 8px',
    border: '1px solid rgba(148,163,184,.20)',
    borderRadius: '10px',
    background: 'rgba(30,41,59,.72)',
    color: '#cbd5e1',
    fontSize: '12px',
    fontWeight: 850,
    cursor: 'pointer'
  },

  segmentButtonActive: {
    borderColor: '#6366f1',
    background: 'rgba(79,70,229,.28)',
    color: '#ffffff'
  },

  range: {
    width: '100%',
    accentColor: '#6366f1'
  },

  toolGrid: {
    marginTop: '14px',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '8px'
  },

  toolButton: {
    minHeight: '42px',
    padding: '0 10px',
    border: '1px solid rgba(148,163,184,.20)',
    borderRadius: '11px',
    background: 'rgba(30,41,59,.72)',
    color: '#e2e8f0',
    fontSize: '12px',
    fontWeight: 850,
    cursor: 'pointer'
  },

  toolButtonActive: {
    borderColor: '#38bdf8',
    color: '#bae6fd',
    background: 'rgba(14,116,144,.22)'
  },

  actions: {
    marginTop: '16px',
    display: 'grid',
    gridTemplateColumns: '1fr 1.25fr',
    gap: '9px'
  },

  secondaryButton: {
    minHeight: '50px',
    padding: '0 12px',
    border: '1px solid rgba(148,163,184,.25)',
    borderRadius: '13px',
    background: 'rgba(30,41,59,.76)',
    color: '#e2e8f0',
    fontSize: '13px',
    fontWeight: 900,
    cursor: 'pointer'
  },

  sendButton: {
    minHeight: '50px',
    padding: '0 15px',
    border: 0,
    borderRadius: '13px',
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 950,
    cursor: 'pointer',
    boxShadow: '0 12px 26px rgba(5,150,105,.24)'
  },

  footer: {
    marginTop: '16px',
    paddingTop: '13px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    borderTop: '1px solid rgba(148,163,184,.14)',
    color: '#64748b',
    fontSize: '10px',
    lineHeight: 1.4,
    wordBreak: 'break-all'
  }
};
